// Baseline design reference (iPhone 14 class device).
export const WELCOME_REFERENCE_WIDTH = 390;
export const WELCOME_REFERENCE_HEIGHT = 844;

export const WELCOME_MAX_CONTENT_WIDTH = 430;

const MIN_WIDTH_SCALE = 0.82;
const MAX_WIDTH_SCALE = 1.15;
const MIN_HEIGHT_SCALE = 0.88;
const MAX_HEIGHT_SCALE = 1.1;

export function getWelcomeWidthScale(screenWidth: number): number {
  const scale = screenWidth / WELCOME_REFERENCE_WIDTH;
  return Math.min(MAX_WIDTH_SCALE, Math.max(MIN_WIDTH_SCALE, scale));
}

export function getWelcomeHeightScale(screenHeight: number): number {
  const scale = screenHeight / WELCOME_REFERENCE_HEIGHT;
  return Math.min(MAX_HEIGHT_SCALE, Math.max(MIN_HEIGHT_SCALE, scale));
}

export function scaleWelcome(size: number, screenWidth: number): number {
  return Math.round(size * getWelcomeWidthScale(screenWidth));
}

export function scaleWelcomeVertical(
  size: number,
  screenHeight: number,
  screenWidth: number
): number {
  const widthScale = getWelcomeWidthScale(screenWidth);
  const heightScale = getWelcomeHeightScale(screenHeight);
  return Math.round(size * ((widthScale + heightScale) / 2));
}

export function getWelcomeLayoutWidth(screenWidth: number): number {
  return Math.min(screenWidth, WELCOME_MAX_CONTENT_WIDTH);
}

export function getWelcomeHorizontalPadding(screenWidth: number): number {
  return scaleWelcome(32, screenWidth);
}

export function getWelcomeTextWidth(screenWidth: number): number {
  return Math.max(
    getWelcomeLayoutWidth(screenWidth) - getWelcomeHorizontalPadding(screenWidth),
    240
  );
}

export interface WelcomeHeroLayout {
  panelLift: number;
  chargerHeadlineOffset: number;
  mountainBottom: number;
  chargerTop: number;
  blackGapFill: number;
  chargerWidth: number;
  chargerHeight: number;
}

export function getWelcomeHeroLayout(
  screenWidth: number,
  screenHeight: number
): WelcomeHeroLayout {
  const widthScale = getWelcomeWidthScale(screenWidth);
  const heightScale = getWelcomeHeightScale(screenHeight);

  const panelLift = Math.round(72 * heightScale);
  const chargerHeadlineOffset = Math.round(36 * widthScale);
  const mountainBottom = Math.round(248 * heightScale) + panelLift;
  const chargerTop =
    Math.round(-68 * heightScale) - panelLift + chargerHeadlineOffset;

  return {
    panelLift,
    chargerHeadlineOffset,
    mountainBottom,
    chargerTop,
    blackGapFill: Math.round(88 * heightScale),
    chargerWidth: Math.round(40 * widthScale),
    chargerHeight: Math.round(90 * heightScale),
  };
}

export interface WelcomeContentLayout {
  widthScale: number;
  layoutWidth: number;
  contentWidth: number;
  containerPaddingHorizontal: number;
  containerPaddingTop: number;
  containerPaddingBottomMin: number;
  headerMarginBottom: number;
  cardsRowMarginTop: number;
  cardColumnGap: number;
  cardWidth: number;
  cardTextWidth: number;
  buttonTextWidth: number;
  footerButtonWidth: number;
  actionButtonHeight: number;
  actionButtonPaddingHorizontal: number;
  actionButtonPaddingVertical: number;
  cardTitleMarginTop: number;
  cardDescriptionMarginTop: number;
  cardIconMarginBottom: number;
  actionButtonMarginTop: number;
  footerMarginTop: number;
  footerPromptMarginBottom: number;
  subheadlineFontSize: number;
  subheadlineLineHeight: number;
  subheadlineMarginTop: number;
  cardDescriptionFontSize: number;
  cardDescriptionLineHeight: number;
  footerPromptFontSize: number;
  footerPromptLineHeight: number;
  markerIcon: { width: number; height: number };
  houseIcon: { width: number; height: number };
  hostStationIconSize: number;
}

export function getWelcomeContentLayout(
  screenWidth: number,
  screenHeight: number
): WelcomeContentLayout {
  const widthScale = getWelcomeWidthScale(screenWidth);
  const layoutWidth = getWelcomeLayoutWidth(screenWidth);
  const containerPaddingHorizontal = scaleWelcome(12, screenWidth);
  const contentWidth = layoutWidth - containerPaddingHorizontal * 2;
  const cardColumnGap = Math.round(contentWidth * 0.082);
  const cardWidth = (contentWidth - cardColumnGap) / 2;

  return {
    widthScale,
    layoutWidth,
    contentWidth,
    containerPaddingHorizontal,
    containerPaddingTop: scaleWelcomeVertical(2, screenHeight, screenWidth),
    containerPaddingBottomMin: scaleWelcome(12, screenWidth),
    headerMarginBottom: scaleWelcomeVertical(20, screenHeight, screenWidth),
    cardsRowMarginTop: scaleWelcome(4, screenWidth),
    cardColumnGap,
    cardWidth,
    cardTextWidth: cardWidth - scaleWelcome(8, screenWidth),
    buttonTextWidth: cardWidth - scaleWelcome(16, screenWidth),
    footerButtonWidth: Math.min(contentWidth, scaleWelcome(320, screenWidth)),
    actionButtonHeight: scaleWelcome(44, screenWidth),
    actionButtonPaddingHorizontal: scaleWelcome(12, screenWidth),
    actionButtonPaddingVertical: scaleWelcome(12, screenWidth),
    cardTitleMarginTop: scaleWelcome(8, screenWidth),
    cardDescriptionMarginTop: scaleWelcome(8, screenWidth),
    cardIconMarginBottom: scaleWelcome(4, screenWidth),
    actionButtonMarginTop: scaleWelcome(12, screenWidth),
    footerMarginTop: scaleWelcomeVertical(24, screenHeight, screenWidth),
    footerPromptMarginBottom: scaleWelcome(12, screenWidth),
    subheadlineFontSize: scaleWelcome(14, screenWidth),
    subheadlineLineHeight: scaleWelcome(20, screenWidth),
    subheadlineMarginTop: scaleWelcome(10, screenWidth),
    cardDescriptionFontSize: scaleWelcome(12, screenWidth),
    cardDescriptionLineHeight: scaleWelcome(17, screenWidth),
    footerPromptFontSize: scaleWelcome(13, screenWidth),
    footerPromptLineHeight: scaleWelcome(18, screenWidth),
    markerIcon: {
      width: scaleWelcome(24, screenWidth),
      height: scaleWelcome(33, screenWidth),
    },
    houseIcon: {
      width: scaleWelcome(35, screenWidth),
      height: scaleWelcome(33, screenWidth),
    },
    hostStationIconSize: scaleWelcome(32, screenWidth),
  };
}
